import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import { canAccessModule, canPerformAction } from "../lib/access";
import { type AssetId, assetIdSchema } from "../lib/workIds";

export type AssetPickerOption = {
  id: AssetId;
  name: string;
  assetNumber: string | null;
  kind: "ASSET" | "LOCATION";
  /** Public ID of this option's direct parent, or `null` at the root. Used
   * only to build a name-only ancestor path — never displayed itself. */
  parentId: AssetId | null;
};

const responseSchema = z.object({
  options: z.array(
    z.object({
      id: assetIdSchema,
      name: z.string(),
      assetNumber: z.string().nullable(),
      kind: z.enum(["ASSET", "LOCATION"]),
      parentId: assetIdSchema.nullable(),
    }),
  ),
});

export function useAssetPickerQuery() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const allowed = Boolean(
    selectedFacility &&
      (canAccessModule(selectedFacility, "ASSETS") ||
        canPerformAction(selectedFacility, "CREATE_REQUEST") ||
        canPerformAction(selectedFacility, "CREATE_WORK_ORDER") ||
        canPerformAction(selectedFacility, "EDIT_OWN_REQUEST")),
  );

  const query = useQuery({
    queryKey: ["asset-picker", selectedCustomer?.id, selectedFacility?.id],
    queryFn: async () => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["asset-picker"].$get({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
        },
      });
      if (!response.ok)
        throw new Error(`Failed to load assets: ${response.status}`);
      return responseSchema.parse(await response.json());
    },
    enabled: Boolean(selectedCustomer && selectedFacility && allowed),
    meta: { errorMessage: "Failed to load assets." },
  });

  return {
    ...query,
    /** The scope is resolved but this role can't load pickable assets, so
     * the query will never run. A scope that is still resolving is not
     * unavailable — it is just not loaded yet. */
    isUnavailable: Boolean(selectedCustomer && selectedFacility && !allowed),
  };
}
